import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Building2, ChevronDown, CreditCard, Landmark, MapPin, Plus, Sparkles, Trash2 } from "lucide-react";

const packages = [
  { key: "standard-3", type: "standard", months: 3, labelKey: "pricing.standard", note: { ro: "3 luni", en: "3 months" } },
  { key: "standard-6", type: "standard", months: 6, labelKey: "pricing.standard", note: { ro: "6 luni -3%", en: "6 months -3%" } },
  { key: "standard-12", type: "standard", months: 12, labelKey: "pricing.standard", note: { ro: "12 luni -5%", en: "12 months -5%" } },
  { key: "seasonal", type: "seasonal", months: 3, labelKey: "pricing.seasonal", note: { ro: "luni exacte, tarif sezonier", en: "exact months, seasonal tariff" } },
  { key: "short", type: "short", months: 1, labelKey: "pricing.short", note: { ro: "targuri / expozitii pe zi", en: "events / exhibitions per day" } },
];

function currentMonthValue() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function newActivity(category = "") {
  return { category, surface_m2: 50, space_name: "" };
}

function newLocation(category = "", user = null) {
  return {
    venue_name: user?.venue_name || user?.business_name || "",
    business_address: user?.business_address || "",
    county: user?.county || "",
    city: user?.city || "",
    is_rural: false,
    activities: [newActivity(category)],
  };
}

function locationsFromUser(user, fallbackCategory = "") {
  if (user?.locations?.length) {
    return user.locations.map((loc) => ({
      venue_name: loc.venue_name || "",
      business_address: loc.business_address || "",
      county: loc.county || "",
      city: loc.city || "",
      is_rural: Boolean(loc.is_rural),
      activities: (loc.activities?.length ? loc.activities : [newActivity(fallbackCategory)]).map((a) => ({
        category: a.category || fallbackCategory,
        surface_m2: Number(a.surface_m2 || 50),
        space_name: a.space_name || "",
      })),
    }));
  }
  return [newLocation(fallbackCategory, user)];
}

export default function LicenseCalculator() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const [tariffs, setTariffs] = useState([]);
  const [months, setMonths] = useState(3);
  const [days, setDays] = useState(1);
  const [packageType, setPackageType] = useState("standard");
  const [licenseStartMonth, setLicenseStartMonth] = useState(currentMonthValue);
  const [locations, setLocations] = useState([]);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("bank_transfer");
  const [cardPaymentsEnabled, setCardPaymentsEnabled] = useState(false);
  const [cardPaymentsMode, setCardPaymentsMode] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [tariffError, setTariffError] = useState("");
  const [confirmations, setConfirmations] = useState({ company: false, representative: false, electronic: false });
  const visibleTariffs = useMemo(() => tariffs.filter((tr) => packageType === "short" ? tr.unit_type === "day" : tr.unit_type !== "day"), [tariffs, packageType]);
  const tariffCategories = useMemo(() => visibleTariffs.map((tr) => tr.category), [visibleTariffs]);
  const startMonthOptions = useMemo(() => {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat(lang === "en" ? "en-US" : "ro-RO", { month: "long", year: "numeric" });
    return Array.from({ length: 24 }, (_, offset) => {
      const month = new Date(now.getFullYear(), now.getMonth() + offset, 1);
      return {
        value: `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, "0")}`,
        label: formatter.format(month),
      };
    });
  }, [lang]);

  useEffect(() => {
    const first = visibleTariffs[0]?.category || "";
    if (!first) return;
    setLocations((items) => items.map((loc) => ({
      ...loc,
      activities: loc.activities.map((activity) => (
        tariffCategories.includes(activity.category) ? activity : { ...activity, category: first }
      )),
    })));
  }, [visibleTariffs, tariffCategories]);

  useEffect(() => {
    let alive = true;
    api.get("/payments/config")
      .then(({ data }) => {
        if (alive) {
          setCardPaymentsEnabled(Boolean(data.card_payments_enabled));
          setCardPaymentsMode(data.card_payments_mode || null);
        }
      })
      .catch(() => {
        if (alive) {
          setCardPaymentsEnabled(false);
          setCardPaymentsMode(null);
        }
      });
    api.get("/tariffs")
      .then(({ data }) => {
        if (!alive) return;
        setTariffError("");
        setTariffs(data);
        setLocations(locationsFromUser(user, data[0]?.category || ""));
      })
      .catch(() => {
        if (!alive) return;
        setTariffError("Nu pot incarca tarifele momentan. Verifica daca backend-ul este pornit si reincarca pagina.");
        setTariffs([]);
        setLocations(locationsFromUser(user, ""));
        setQuote(null);
      });
    return () => { alive = false; };
  }, [user]);

  const payloadLocations = useMemo(() => locations.map((loc) => ({
    venue_name: loc.venue_name,
    business_address: loc.business_address,
    county: loc.county,
    city: loc.city,
    is_rural: Boolean(loc.is_rural),
    activities: loc.activities.map((a) => ({ category: a.category, surface_m2: Number(a.surface_m2 || 0), space_name: a.space_name || undefined })),
  })), [locations]);

  useEffect(() => {
    if (!payloadLocations.length || payloadLocations.some((l) => !l.business_address || l.activities.some((a) => !a.category || !a.surface_m2))) return;
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.post("/licenses/quote", {
          months: Number(months),
          days: Number(days),
          package_type: packageType,
          license_start_day: packageType === "short" ? undefined : `${licenseStartMonth}-01`,
          locations: payloadLocations,
        });
        setQuote(data);
      } catch {
        setQuote(null);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [payloadLocations, months, days, packageType, licenseStartMonth]);

  const updateLocation = (index, patch) => setLocations((items) => items.map((loc, i) => (i === index ? { ...loc, ...patch } : loc)));
  const updateActivity = (locIndex, actIndex, patch) => setLocations((items) => items.map((loc, i) => {
    if (i !== locIndex) return loc;
    return { ...loc, activities: loc.activities.map((activity, j) => (j === actIndex ? { ...activity, ...patch } : activity)) };
  }));
  const addLocation = () => setLocations((items) => [...items, newLocation(tariffs[0]?.category || "", user)]);
  const removeLocation = (index) => setLocations((items) => items.length > 1 ? items.filter((_, i) => i !== index) : items);
  const addActivity = (locIndex) => setLocations((items) => items.map((loc, i) => {
    if (i !== locIndex) return loc;
    const category = loc.activities[loc.activities.length - 1]?.category || tariffCategories[0] || "";
    if (!category) {
      toast.message(t("pricing.allActivitiesAdded"));
      return loc;
    }
    return { ...loc, activities: [...loc.activities, newActivity(category)] };
  }));
  const removeActivity = (locIndex, actIndex) => setLocations((items) => items.map((loc, i) => (
    i === locIndex && loc.activities.length > 1 ? { ...loc, activities: loc.activities.filter((_, j) => j !== actIndex) } : loc
  )));

  const buy = async () => {
    if (!user) {
      toast.message(t("pricing.requiresAuth"));
      nav("/login?next=" + encodeURIComponent("/?focus=calc"));
      return;
    }
    if (!confirmations.company || !confirmations.representative || !confirmations.electronic) {
      toast.error(t("pricing.confirmBeforePay"));
      return;
    }
    setLoading(true);
    try {
      const payload = {
        months: Number(months),
        days: Number(days),
        package_type: packageType,
        license_start_day: packageType === "short" ? undefined : `${licenseStartMonth}-01`,
        locations: payloadLocations,
        confirm_company_data: confirmations.company,
        confirm_representative_right: confirmations.representative,
        request_electronic_contract_license: confirmations.electronic,
      };
      if (paymentMethod === "bank_transfer") {
        const { data } = await api.post("/licenses/bank-transfer/instructions", payload);
        setPaymentInfo({ ...data, type: "bank_transfer" });
        toast.success(t("pricing.bankInstructionsReady"));
        return;
      }
      const { data } = await api.post("/licenses/checkout", { ...payload, origin_url: window.location.origin });
      setPaymentInfo({ ...data, type: "online_card" });
      toast.success(t("pricing.redirectingToPayment"));
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  const reportBankTransferPaid = async () => {
    if (!paymentInfo || paymentInfo.type !== "bank_transfer") return;
    if (!confirmations.company || !confirmations.representative || !confirmations.electronic) {
      toast.error(t("pricing.confirmBeforePay"));
      return;
    }
    setLoading(true);
    try {
      const payload = {
        months: Number(months),
        days: Number(days),
        package_type: packageType,
        license_start_day: packageType === "short" ? undefined : `${licenseStartMonth}-01`,
        locations: payloadLocations,
        confirm_company_data: confirmations.company,
        confirm_representative_right: confirmations.representative,
        request_electronic_contract_license: confirmations.electronic,
      };
      const { data } = await api.post("/licenses/bank-transfer", payload);
      setPaymentInfo({ ...data, type: "bank_transfer" });
      toast.success(data.existing_pending ? t("pricing.transferExisting") : t("pricing.transferCreated"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  const monthly = useMemo(() => {
    if (!quote) return "0.00";
    const divisor = quote.package_type === "short" ? Math.max(1, quote.days || 1) : Math.max(1, quote.billed_months || quote.months);
    return (quote.total / divisor).toFixed(2);
  }, [quote]);

  return (
    <div data-testid="license-calculator" className="rounded-2xl border border-white/5 bg-[#121212] p-4 sm:rounded-3xl sm:p-8">
      {tariffError && (
        <div className="mb-5 rounded-2xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-100" data-testid="tariff-load-error">
          {tariffError}
        </div>
      )}
      <div className="grid gap-5 lg:grid-cols-[1.35fr_1fr]">
        <div className="space-y-5">
          <div>
            <label className="mb-2 block text-xs uppercase tracking-widest text-zinc-500">{t("pricing.packageLabel")}</label>
            <div className="grid gap-3 sm:grid-cols-2">
              {packages.map((pkg) => {
                const active = packageType === pkg.type && (pkg.type === "seasonal" || pkg.type === "short" || months === pkg.months);
                return (
                  <button
                    type="button"
                    key={pkg.key}
                    data-testid={`calc-package-${pkg.key}`}
                    onClick={() => { setMonths(pkg.months); setPackageType(pkg.type); }}
                    className={`min-h-[78px] rounded-xl px-4 py-3 text-left text-sm font-semibold transition ${active ? "bg-[#D4A23A] text-black" : "border border-white/10 text-zinc-300 hover:bg-white/5"}`}
                  >
                    <span className="block leading-tight">{t(pkg.labelKey)}</span>
                    <span className={`mt-1.5 block text-[11px] leading-snug ${active ? "text-black/70" : "text-zinc-500"}`}>{pkg.note[lang] || pkg.note.ro}</span>
                  </button>
                );
              })}
            </div>
            {packageType === "seasonal" && (
              <div className="mt-3">
                <label className="mb-2 block text-xs uppercase tracking-widest text-zinc-500">{t("pricing.seasonalMonths")}</label>
                <select
                  value={months}
                  onChange={(e) => setMonths(Number(e.target.value))}
                  className="lime-focus w-full rounded-xl border border-white/10 bg-[#0A0A0A] px-4 py-3.5 text-sm text-white focus:outline-none"
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <option key={m} value={m}>{m} {m === 1 ? (lang === "en" ? "month" : "luna") : t("pricing.monthLabel")}</option>
                  ))}
                </select>
              </div>
            )}
            {packageType === "short" && (
              <div className="mt-3">
                <label className="mb-2 block text-xs uppercase tracking-widest text-zinc-500">Numar zile activitate</label>
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={days}
                  onFocus={(e) => e.currentTarget.select()}
                  onChange={(e) => setDays(Number(e.target.value || 1))}
                  className="lime-focus w-full rounded-xl border border-white/10 bg-[#0A0A0A] px-4 py-3.5 text-sm text-white focus:outline-none"
                />
              </div>
            )}
            {packageType !== "short" && (
              <div className="mt-3 rounded-xl border border-white/10 bg-black/20 p-3">
                <label className="mb-2 block text-xs uppercase tracking-widest text-zinc-500" htmlFor="license-start-month">
                  {t("pricing.startMonth")}
                </label>
                <div className="relative">
                  <select
                    id="license-start-month"
                    data-testid="calc-license-start-month"
                    value={licenseStartMonth}
                    onChange={(e) => {
                      setLicenseStartMonth(e.target.value);
                      setPaymentInfo(null);
                    }}
                    className="lime-focus w-full appearance-none rounded-xl border border-white/10 bg-[#0A0A0A] px-4 py-3.5 pr-10 text-sm capitalize text-white focus:outline-none"
                  >
                    {startMonthOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <ChevronDown size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                </div>
                <p className="mt-2 text-xs leading-relaxed text-zinc-400">
                  {licenseStartMonth === currentMonthValue()
                    ? t("pricing.currentMonthRule")
                    : t("pricing.futureMonthRule")}
                </p>
              </div>
            )}
          </div>

          {locations.map((loc, locIndex) => (
            <div key={locIndex} className="rounded-2xl border border-white/10 bg-black/20 p-4">
              <div className="mb-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-white">
                  <Building2 size={17} className="text-[#D4A23A]" />
                  {t("pricing.venue")} {locIndex + 1}
                </div>
                {locations.length > 1 && (
                  <button type="button" onClick={() => removeLocation(locIndex)} className="rounded-lg border border-white/10 p-2 text-zinc-400 hover:text-red-300" title={t("common.delete")}>
                    <Trash2 size={16} />
                  </button>
                )}
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <Field value={loc.venue_name} onChange={(v) => updateLocation(locIndex, { venue_name: v })} placeholder={t("pricing.venueName")} />
                <Field value={loc.business_address} onChange={(v) => updateLocation(locIndex, { business_address: v })} placeholder={t("pricing.venueAddress")} icon={<MapPin size={15} />} />
                <Field value={loc.county} onChange={(v) => updateLocation(locIndex, { county: v })} placeholder={t("pricing.county")} />
                <Field value={loc.city} onChange={(v) => updateLocation(locIndex, { city: v })} placeholder={t("pricing.city")} />
              </div>
              <label className="mt-3 flex items-center gap-2 rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-xs text-zinc-300">
                <input
                  type="checkbox"
                  checked={Boolean(loc.is_rural)}
                  onChange={(e) => updateLocation(locIndex, { is_rural: e.target.checked })}
                  className="accent-[#D4A23A]"
                />
                Mediu rural - aplica regula din tabel
              </label>

              <div className="mt-4 space-y-3">
                {loc.activities.map((activity, actIndex) => (
                  <div key={actIndex} className="grid gap-3 rounded-xl border border-white/5 bg-[#0A0A0A] p-3 sm:grid-cols-[1fr_1fr_150px_42px]">
                    <Field value={activity.space_name || ""} onChange={(v) => updateActivity(locIndex, actIndex, { space_name: v })} placeholder="Zona / subcategorie (ex. interior, terasa)" />
                    <div className="relative">
                      <select
                        value={activity.category}
                        onChange={(e) => updateActivity(locIndex, actIndex, { category: e.target.value })}
                        className="lime-focus w-full appearance-none rounded-xl border border-white/10 bg-black px-4 py-3 pr-10 text-sm text-white focus:outline-none"
                      >
                        <option value="" disabled>{t("pricing.chooseActivity")}</option>
                        {visibleTariffs.map((tr) => (
                          <option key={tr.id} value={tr.category}>
                            {tr.category}
                          </option>
                        ))}
                      </select>
                      <ChevronDown size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                    </div>
                    <input
                      type="number"
                      min={1}
                      step={1}
                      inputMode="numeric"
                      value={activity.surface_m2}
                      onFocus={(e) => e.currentTarget.select()}
                      onChange={(e) => updateActivity(locIndex, actIndex, { surface_m2: Number(e.target.value) })}
                      className="lime-focus rounded-xl border border-white/10 bg-black px-4 py-3 text-sm text-white focus:outline-none"
                      placeholder="mp"
                    />
                    <button type="button" onClick={() => removeActivity(locIndex, actIndex)} disabled={loc.activities.length === 1} className="rounded-xl border border-white/10 py-3 text-zinc-400 disabled:opacity-30 hover:text-red-300 sm:py-0" title={t("pricing.removeActivity")}>
                      <Trash2 size={16} className="mx-auto" />
                    </button>
                  </div>
                ))}
                <button type="button" onClick={() => addActivity(locIndex)} className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-[#D4A23A]/30 px-3 py-2.5 text-xs font-semibold text-[#D4A23A] hover:bg-[#D4A23A]/10 sm:w-auto">
                  <Plus size={14} /> Adauga zona / activitate in aceeasi licenta
                </button>
              </div>
            </div>
          ))}

          <button type="button" onClick={addLocation} className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-white/5 px-4 py-3 text-sm font-semibold text-white hover:bg-white/10 sm:w-auto">
            <Plus size={16} /> {t("pricing.addVenue")}
          </button>
        </div>

        <div className="flex flex-col justify-between rounded-2xl bg-gradient-to-br from-[#D4A23A]/15 via-[#0A0A0A] to-[#0A0A0A] p-5 ring-1 ring-[#D4A23A]/20 sm:p-6">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#D4A23A]">
              <Sparkles size={14} /> {t("pricing.total")}
            </div>
            <p className="mt-3 font-display text-4xl font-black text-white sm:text-6xl" data-testid="calc-total">
              {quote ? quote.total.toFixed(2) : "-"}
              <span className="ml-1 text-lg text-zinc-400 sm:text-xl">{t("common.ron")}</span>
            </p>
            <p className="mt-1 text-sm text-zinc-500">{monthly} {t("common.ron")}/{quote?.package_type === "short" ? "zi" : t("pricing.monthlyBilled")}</p>

            {quote && (
              <div className="mt-6 space-y-2 text-xs text-zinc-400">
                <Row label={t("pricing.venues")} value={`${quote.locations?.length || 0}`} />
                <Row label={t("pricing.activities")} value={`${quote.locations?.reduce((n, l) => n + (l.activities?.length || 0), 0) || 0}`} />
                <Row label={t("pricing.declaredSurface")} value={`${quote.surface_m2} mp`} />
                <Row label={t("pricing.billedSurface")} value={`${quote.billed_surface_m2 || quote.surface_m2} mp`} />
                {quote.package_type === "short" ? (
                  <Row label="Zile activitate" value={`${quote.days}`} />
                ) : (
                  <>
                    <Row label={t("pricing.selectedMonths")} value={`${quote.months}`} />
                    <Row label={t("pricing.legalBilledMonths")} value={`${quote.billed_months}`} />
                  </>
                )}
                <Row label={t("pricing.package")} value={quote.package_type === "short" ? "Durata scurta" : (quote.package_type === "seasonal" ? t("pricing.seasonal") : t("pricing.standard"))} />
                {Number(quote.discount_percent || 0) > 0 && (
                  <>
                    <Row label={t("pricing.subtotal")} value={`${quote.subtotal_before_discount.toFixed(2)} RON`} />
                    <Row label={`${t("pricing.discount")} ${quote.discount_percent}%`} value={`-${quote.discount_amount.toFixed(2)} RON`} />
                  </>
                )}
                <Row label={t("pricing.validity")} value={`${formatDate(quote.license_start)} - ${formatDate(quote.license_end)}`} />
                <Row label={t("pricing.yearly")} value={`${quote.yearly_price.toFixed(2)} RON`} />
              </div>
            )}

            {quote?.locations?.length > 0 && (
              <div className="mt-5 space-y-3">
                {quote.locations.map((loc, i) => (
                  <div key={i} className="rounded-xl border border-white/10 bg-black/20 p-3 text-xs text-zinc-300">
                    <div className="mb-2 font-semibold text-white">{loc.venue_name || `${t("pricing.venue")} ${i + 1}`} - {loc.total.toFixed(2)} RON</div>
                    {loc.activities.map((a, j) => (
                      <div key={j} className="flex flex-col gap-1 border-t border-white/5 py-2 sm:flex-row sm:justify-between">
                        <span>{a.space_name ? `${a.space_name} - ` : ""}{a.category} - {a.surface_m2} {a.unit_label || t("pricing.declaredSqm")} - {a.surface_interval || `${a.billed_surface_m2 || a.surface_m2} ${a.unit_label || "mp"}`}{a.is_rural ? " - rural" : ""}</span>
                        <span>{a.total.toFixed(2)} RON</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          {user && (
            <div className="mt-6 space-y-2 rounded-2xl border border-white/10 bg-black/20 p-3 text-xs text-zinc-300">
              <PaymentCheck checked={confirmations.company} onChange={(v) => setConfirmations((c) => ({ ...c, company: v }))}>{t("pricing.confirmCompany")}</PaymentCheck>
              <PaymentCheck checked={confirmations.representative} onChange={(v) => setConfirmations((c) => ({ ...c, representative: v }))}>{t("pricing.confirmRepresentative")}</PaymentCheck>
              <PaymentCheck checked={confirmations.electronic} onChange={(v) => setConfirmations((c) => ({ ...c, electronic: v }))}>{t("pricing.confirmElectronic")}</PaymentCheck>
            </div>
          )}

          {user && (
            <div className="mt-5 rounded-2xl border border-white/10 bg-black/20 p-3">
              <p className="mb-3 text-xs uppercase tracking-widest text-zinc-500">{t("pricing.paymentMethod")}</p>
              <div className={`grid gap-2 ${cardPaymentsEnabled ? "sm:grid-cols-2" : ""}`}>
                <PaymentMethodButton active={paymentMethod === "bank_transfer"} icon={<Landmark size={16} />} label={t("pricing.bankTransfer")} onClick={() => { setPaymentMethod("bank_transfer"); setPaymentInfo(null); }} />
                {cardPaymentsEnabled && (
                  <PaymentMethodButton
                    active={paymentMethod === "online_card"}
                    icon={<CreditCard size={16} />}
                    label={cardPaymentsMode === "test" ? t("pricing.onlineCardTest") : t("pricing.onlineCard")}
                    onClick={() => { setPaymentMethod("online_card"); setPaymentInfo(null); }}
                  />
                )}
              </div>
              {cardPaymentsEnabled && cardPaymentsMode === "test" && (
                <p className="mt-3 text-xs font-medium text-amber-300">{t("pricing.cardTestNotice")}</p>
              )}
            </div>
          )}

          <button
            data-testid="calc-buy-btn"
            disabled={!quote || loading || (user && (!confirmations.company || !confirmations.representative || !confirmations.electronic))}
            onClick={buy}
            className="mt-6 w-full rounded-full bg-[#D4A23A] py-3.5 font-semibold text-black transition hover:bg-[#E2B85C] disabled:cursor-not-allowed disabled:opacity-40"
          >
            {loading ? t("common.loading") : paymentMethod === "bank_transfer" ? t("pricing.viewBankInstructions") : t("pricing.payOnline")}
          </button>
          {!user && <p className="mt-2 text-center text-[11px] text-zinc-500">{t("pricing.requiresAuth")}</p>}

          {paymentInfo && (
            paymentInfo.type === "bank_transfer" ? (
              <div className="mt-4 space-y-3 rounded-2xl border border-[#D4A23A]/30 bg-[#D4A23A]/10 p-4 text-sm text-zinc-200" data-testid="bank-payment-instructions">
                <div>
                  <p className="font-semibold text-white">{paymentInfo.existing_pending ? t("pricing.transferExisting") : t("pricing.bankGenerated")}</p>
                  <p className="mt-1 text-xs text-zinc-400">{t("pricing.bankGeneratedSub")}</p>
                </div>
                <div className="space-y-2 text-xs">
                  <Row label={t("pricing.amountDue")} value={`${Number(paymentInfo.amount || 0).toFixed(2)} ${String(paymentInfo.currency || "RON").toUpperCase()}`} />
                  <Row label={t("pricing.beneficiary")} value={paymentInfo.bank_account_name || "backend/.env"} />
                  <Row label={t("pricing.bankIban")} value={paymentInfo.bank_iban || "backend/.env"} />
                  <Row label={t("pricing.bankName")} value={paymentInfo.bank_name || "backend/.env"} />
                  <Row label={t("pricing.paymentReference")} value={paymentInfo.bank_reference} />
                  {paymentInfo.smartbill_invoice_series && paymentInfo.smartbill_invoice_number && (
                    <Row label={t("pricing.smartbillInvoice")} value={`${paymentInfo.smartbill_invoice_series} ${paymentInfo.smartbill_invoice_number}`} />
                  )}
                </div>
                <p className="rounded-xl border border-white/10 bg-black/20 p-3 text-xs text-zinc-300">{paymentInfo.instructions}</p>
                {paymentInfo.payment_status === "draft_bank_transfer" ? (
                  <button
                    type="button"
                    disabled={loading}
                    onClick={reportBankTransferPaid}
                    className="w-full rounded-full bg-[#D4A23A] px-4 py-3 text-sm font-black text-black transition hover:bg-[#E2B85C] disabled:opacity-50"
                  >
                    {loading ? t("common.loading") : t("pricing.reportBankPaid")}
                  </button>
                ) : (
                  <p className="rounded-xl border border-[#D4A23A]/20 bg-black/20 p-3 text-xs text-[#D4A23A]">{t("pricing.bankReported")}</p>
                )}
              </div>
            ) : (
              <div className="mt-4 rounded-2xl border border-[#D4A23A]/30 bg-[#D4A23A]/10 p-4 text-sm text-zinc-200" data-testid="online-payment-instructions">
                <p className="font-semibold text-white">{t("pricing.onlineStarted")}</p>
                <p className="mt-2 text-xs text-zinc-400">{t("pricing.onlineStartedSub")}</p>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ value, onChange, placeholder, icon }) {
  return (
    <div className="relative">
      {icon && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">{icon}</span>}
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`lime-focus w-full rounded-xl border border-white/10 bg-[#0A0A0A] px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none ${icon ? "pl-9" : ""}`}
      />
    </div>
  );
}

function PaymentCheck({ checked, onChange, children }) {
  return (
    <label className="flex cursor-pointer items-start gap-2">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#D4A23A]" />
      <span>{children}</span>
    </label>
  );
}

function PaymentMethodButton({ active, icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center justify-center gap-2 rounded-xl border px-3 py-3 text-sm font-semibold transition ${
        active ? "border-[#D4A23A] bg-[#D4A23A] text-black" : "border-white/10 text-zinc-300 hover:bg-white/5"
      }`}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4 border-b border-white/5 pb-2">
      <span className="text-zinc-500">{label}</span>
      <span className="text-right text-white">{value}</span>
    </div>
  );
}

function formatDate(value) {
  if (!value) return "-";
  const isoDate = String(value).slice(0, 10);
  const [year, month, day] = isoDate.split("-");
  if (!year || !month || !day) return isoDate;
  return `${Number(day)}.${month}.${year}`;
}
